/**
 * tutorialProgress — persistencia del progreso del tutorial (KAN-232).
 *
 * Solo caché del browser (localStorage), por usuario — mismo patrón que el
 * modal Novedades (`mcd:changelog_lastSeenId:{userId}`) y el onboarding del
 * puesto (`mcd:mapa:onboarding:puesto:{userId}`). Nada viaja al server.
 *
 * Funciones puras + lectura/escritura directa de localStorage: pensadas para
 * usarse desde componentes sin contexto (y testeables con jsdom).
 */

import type {
  TutorialModuleDef,
  TutorialModuleProgress,
  TutorialState,
} from './types';
import type { Action, Module } from '../../types/permissions';

export const STORAGE_KEY = (userId: string) => `mcd:tutorial:${userId}`;

/** Días por defecto del snooze "Continuar más tarde" (y del cierre con X del recordatorio). */
export const SNOOZE_DIAS_DEFAULT = 14;

const emptyState = (): TutorialState => ({ version: 1, dismissed: false, modules: {} });

/** Lee el estado del usuario; tolera basura/corrupción devolviendo default. */
export function getTutorialState(userId: string): TutorialState {
  if (!userId) return emptyState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY(userId));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as TutorialState;
    if (!parsed || typeof parsed !== 'object' || !parsed.modules) return emptyState();
    return { ...emptyState(), ...parsed, modules: { ...parsed.modules } };
  } catch {
    return emptyState();
  }
}

export function setTutorialState(userId: string, state: TutorialState): void {
  if (!userId) return;
  try {
    localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(state));
  } catch {
    // localStorage lleno/bloqueado: el tutorial degrada a no-persistente.
  }
}

/** Mutación funcional helper: lee → transforma → persiste. */
function update(userId: string, fn: (s: TutorialState) => TutorialState): TutorialState {
  const next = fn(getTutorialState(userId));
  setTutorialState(userId, next);
  return next;
}

const moduleProgress = (s: TutorialState, moduleId: string): TutorialModuleProgress =>
  s.modules[moduleId] ?? { status: 'not_started', stepsDone: [] };

export function getModuleStatus(
  userId: string,
  moduleId: string,
): TutorialModuleProgress['status'] {
  return moduleProgress(getTutorialState(userId), moduleId).status;
}

/**
 * Marca un paso como hecho. Si con ese paso se alcanza el total de pasos
 * del módulo, el módulo queda completado. `totalSteps` lo pasa el runner
 * (que conoce la definición viva del contenido).
 */
export function markStepDone(userId: string, moduleId: string, stepId: string, totalSteps: number): void {
  update(userId, s => {
    const prev = moduleProgress(s, moduleId);
    const stepsDone = prev.stepsDone.includes(stepId)
      ? prev.stepsDone
      : [...prev.stepsDone, stepId];
    const status: TutorialModuleProgress['status'] =
      stepsDone.length >= totalSteps ? 'completed' : 'in_progress';
    return {
      ...s,
      modules: {
        ...s.modules,
        [moduleId]: { ...prev, stepsDone, status, updatedAt: new Date().toISOString() },
      },
    };
  });
}

/** Fuerza completado (detección por datos reales o fin de replay). */
export function markModuleCompleted(userId: string, moduleId: string): void {
  update(userId, s => {
    const prev = moduleProgress(s, moduleId);
    return {
      ...s,
      modules: {
        ...s.modules,
        [moduleId]: { ...prev, status: 'completed', updatedAt: new Date().toISOString() },
      },
    };
  });
}

/** "Ya lo viste": registra que el usuario completó/vió el módulo completo. */
export function markModuleSeen(userId: string, moduleId: string): void {
  update(userId, s => {
    const prev = moduleProgress(s, moduleId);
    return {
      ...s,
      modules: {
        ...s.modules,
        [moduleId]: { ...prev, seenAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      },
    };
  });
}

/** "No recordar nuevamente" (botón rojo): apaga los recordatorios para siempre. */
export function dismissReminders(userId: string): void {
  update(userId, s => ({ ...s, dismissed: true }));
}

/** "Continuar más tarde": no molestar por N minutos. */
export function snoozeReminders(userId: string, minutes = SNOOZE_DIAS_DEFAULT * 24 * 60): void {
  const until = new Date(Date.now() + minutes * 60 * 1000).toISOString();
  update(userId, s => ({ ...s, snoozedUntil: until }));
}

/** true si estamos dentro de la ventana de snooze. */
export function isSnoozed(userId: string): boolean {
  const { snoozedUntil } = getTutorialState(userId);
  if (!snoozedUntil) return false;
  return new Date(snoozedUntil).getTime() > Date.now();
}

/**
 * Primer módulo (en orden de aprendizaje) que no está completado.
 * No filtra por permiso — para la sugerencia con permisos usar
 * getSuggestedModule. El hub igualmente muestra los bloqueados en gris.
 */
export function nextPendingModule(userId: string, modules: TutorialModuleDef[]): TutorialModuleDef | null {
  const st = getTutorialState(userId);
  return modules.find(m => moduleProgress(st, m.id).status !== 'completed') ?? null;
}

/**
 * Sugerencia con permisos: primer módulo pendiente cuya acción el rol
 * puede ejecutar. `can` es el can(module, action) de usePermissions.
 */
export function getSuggestedModule(
  userId: string,
  modules: TutorialModuleDef[],
  can: (m: Module, a: Action) => boolean,
): TutorialModuleDef | null {
  const st = getTutorialState(userId);
  return (
    modules.find(
      m => moduleProgress(st, m.id).status !== 'completed' && can(m.permission.module, m.permission.action),
    ) ?? null
  );
}

/**
 * Auto-completado por datos reales: para cada módulo sin estado previo
 * corre su completionCheck (repos locales) y si los datos ya existen lo
 * marca completado. Usuarios con campo/animales/puesto no son molestados.
 */
export async function mergeAutoCompletion(userId: string, modules: TutorialModuleDef[]): Promise<void> {
  const checks = await Promise.all(
    modules.map(async m => {
      const prev = moduleProgress(getTutorialState(userId), m.id);
      if (prev.status !== 'not_started') return false;
      try {
        return await m.completionCheck();
      } catch {
        return false; // DB local aún inicializándose: quedará para la próxima
      }
    }),
  );
  const completados = modules.filter((_, i) => checks[i]);
  if (completados.length === 0) return;
  update(userId, s => {
    const mods = { ...s.modules };
    for (const m of completados) {
      mods[m.id] = {
        ...moduleProgress(s, m.id),
        status: 'completed',
        updatedAt: new Date().toISOString(),
      };
    }
    return { ...s, modules: mods };
  });
}

/** Borra el progreso (para "Reiniciar progreso" del hub / demos). */
export function resetProgress(userId: string): void {
  try {
    localStorage.removeItem(STORAGE_KEY(userId));
  } catch {
    /* noop */
  }
}

/**
 * true si en esta visita debería mostrarse el recordatorio del tutorial:
 * hay módulo sugerido según el rol del usuario, sin descarte ("No recordar
 * nuevamente") ni snooze vigente. Predicado compartido entre el propio
 * TutorialReminder y el modal "Novedades" del dashboard, que le cede el
 * turno — para quien está aprendiendo la app, el tutorial pesa más que el
 * changelog (mismo criterio que OnboardingPuesto con isTutorialActiveForUser).
 * Corre el merge de auto-completado antes de decidir.
 */
export async function shouldRemindTutorial(
  userId: string,
  modules: TutorialModuleDef[],
  can: (m: Module, a: Action) => boolean,
): Promise<boolean> {
  if (getTutorialState(userId).dismissed || isSnoozed(userId)) return false;
  await mergeAutoCompletion(userId, modules);
  return getSuggestedModule(userId, modules, can) !== null;
}

/**
 * true si el tutorial está "activo" para el usuario (no descartado): usado
 * por OnboardingPuesto para cederle el paso — mientras el tutorial viva, el
 * módulo final "El mapa y tu puesto" cumple ese rol y no queremos dos
 * popups pidiendo lo mismo. Si el usuario descarta el tutorial, el popup
 * del puesto vuelve a su comportamiento original.
 */
export function isTutorialActiveForUser(userId: string): boolean {
  return !getTutorialState(userId).dismissed;
}
